<script setup lang="ts">
/**
 * SeoSpreadPatch — a spread on the SEO pages as a patch of fabric, the same stock +
 * fabric as its tile on the app's /spread home (so the site and the app are one
 * patchwork). The slot is the tile's text; a small fan of face-down cards — one per
 * card in the reading — sits in the bottom-right corner (static, box-shadow only,
 * lazy, same-origin back). A link, lifting a hair on hover.
 */
import { RouterLink } from 'vue-router';
import CardFan from '@app/presentation/components/paper/CardFan.vue';
import { CARD_BACK_SRC } from '@app/data/spreadCutouts';

const props = withDefaults(
  defineProps<{
    to: string;
    /** `bg-stock-*` (or `bg-card`) — the patch's leading colour. */
    stock: string;
    /** `fabric-*` role. */
    fabric: string;
    /** Face-down cards to fan (0 = none). */
    fan?: number;
    fanWidth?: number;
  }>(),
  { fan: 0, fanWidth: 34 },
);

const backs = Array.from({ length: Math.min(7, props.fan) }, () => CARD_BACK_SRC);
</script>

<template>
  <RouterLink
    :to="to"
    :class="[
      'patch paper-sheet relative flex h-full flex-col overflow-hidden text-foreground',
      stock,
      fabric,
    ]"
  >
    <div class="relative z-[1] flex flex-1 flex-col">
      <slot />
    </div>
    <span
      v-if="backs.length"
      class="patch-fan absolute bottom-0 right-4 z-[1] block"
      aria-hidden="true"
    >
      <CardFan
        :faces="backs"
        :card-width="fanWidth"
        :spread="10"
      />
    </span>
  </RouterLink>
</template>

<style scoped>
.patch {
  transition: translate 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}
/* The fan is tucked a little past the bottom edge (the patch clips it). */
.patch-fan {
  translate: 0 22%;
}
@media (hover: hover) {
  .patch:hover {
    translate: 0 -3px;
  }
}
.patch:active {
  translate: 0 1px;
}
.patch:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 3px;
}
@media (prefers-reduced-motion: reduce) {
  .patch {
    transition: none;
  }
}
</style>
